import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, ListChecks, Plus } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { CONSULTA_ASIGNABLES, resolutorDeNombres } from "@/componentes/tareas/asignables"
import { CargaEquipo } from "@/componentes/tareas/CargaEquipo"
import { DialogoNuevaTarea } from "@/componentes/tareas/DialogoNuevaTarea"
import { documentoDeTareas, subtituloDeTareas } from "@/componentes/tareas/exportarTareas"
import {
  consultaDeApi,
  escribirFiltros,
  type Filtros,
  FiltrosTareas,
  leerFiltros,
  VISTAS,
} from "@/componentes/tareas/FiltrosTareas"
import { ListaTareas } from "@/componentes/tareas/ListaTareas"
import { PanelTarea } from "@/componentes/tareas/PanelTarea"
import { ResumenTareas } from "@/componentes/tareas/ResumenTareas"
import { TableroTareas } from "@/componentes/tareas/TableroTareas"
import type { EstadoTarea, Tarea } from "@/componentes/tareas/TarjetaTarea"
import { Cajon } from "@/componentes/ui/Cajon"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Tablero de trabajo.
 *
 * No es un backlog de desarrollo: es asignación y seguimiento de trabajo real —
 * «llamar al cliente DC Química y enviar documentación». Las tareas `DEV` son un
 * tipo más, y el filtro de tipo permite seguir el desarrollo sin sacarlo a otra
 * pantalla.
 *
 * **Se arrastra Y se elige.** El selector «Mover a» de cada tarjeta sigue siendo el
 * camino completo —teclado y lector de pantalla— y el arrastre es el añadido. Se
 * construyó en ese orden a propósito: WCAG 2.5.7 exige la alternativa, y quien
 * empieza por el arrastre nunca la escribe.
 *
 * **Cuatro vistas, y «Resumen» es la puerta.** Ahí está lo que a uno le toca, con el
 * mes al lado en compacto. El tablero mueve cinco tarjetas y la lista repasa
 * cincuenta —la misma información con dos densidades—; «Carga» cuenta por persona y
 * exige `tarea:asignar`, porque agrupar por persona sólo dice algo cuando se ven las
 * tareas de todos.
 *
 * ⚠ **El resumen NO es el panel del sistema.** Ese contesta «cómo va el negocio»
 * con indicadores operativos y financieros; éste contesta «qué hago yo hoy». Los dos
 * vivieron juntos un rato y quedaba una pantalla donde había que leer los seis
 * números para saber cuáles hablaban de uno.
 *
 * **El alta es un modal y el detalle una ficha lateral.** Son dos gestos distintos:
 * crear exige atención completa —siete campos y sus soportes— y por eso lo de detrás
 * queda inerte; mirar una tarea es compararla con las demás, y por eso la ficha entra
 * por la derecha sin apagar el tablero, que sigue pulsable con ella abierta.
 *
 * **Se relee solo cada 60 s**, como el buzón. Sin animar las filas: el sondeo haría
 * temblar el tablero cada minuto y esta gente lo mira ocho horas.
 *
 * **Sin actualización optimista:** mover una tarjeta y que el servidor lo rechace
 * dejaría el tablero mintiendo sobre quién tiene qué.
 */

interface Pagina {
  total: number
  limit: number
  offset: number
  items: Tarea[]
}

export function Tareas() {
  const [params, setParams] = useSearchParams()
  const filtros = leerFiltros(params)

  const [creando, setCreando] = useState(false)

  // ⚠ **Qué tarea está abierta vive en la URL**, no en el estado. Es lo que hace que
  // un aviso sea un enlace de verdad: `/tareas?tarea=42` abre esa ficha, se puede
  // copiar y pegar, y el botón de atrás cierra la ficha en vez de salir de la
  // pantalla.
  const enUrl = params.get("tarea")
  const fichaVisible = enUrl !== null

  // Y una copia local que NO se limpia al cerrar. Sin ella la ficha se quedaría
  // vacía durante la animación de salida, y se vería desaparecer el contenido antes
  // que el panel.
  const [abierta, setAbierta] = useState<number | null>(null)
  useEffect(() => {
    if (enUrl !== null) setAbierta(Number(enUrl))
  }, [enUrl])

  const conFicha = (id: number | null): URLSearchParams => {
    const siguiente = new URLSearchParams(params)
    if (id === null) siguiente.delete("tarea")
    else siguiente.set("tarea", String(id))
    return siguiente
  }
  const abrirFicha = (id: number) => setParams(conFicha(id), { replace: true })

  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const consultaAsignables = useQuery(CONSULTA_ASIGNABLES)
  const asignables = consultaAsignables.data ?? []
  const nombreDe = resolutorDeNombres(asignables)

  const consulta = useQuery({
    // La clave lleva TODO lo que cambia la respuesta. Si un filtro faltara aquí,
    // activarlo mostraría la lista anterior cacheada.
    queryKey: ["tareas", filtros],
    queryFn: () => api.get<Pagina>(`/api/v1/tareas?${consultaDeApi(filtros, usuario?.id)}`),
    refetchInterval: 60_000,
  })

  const mover = useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: EstadoTarea }) =>
      api.patch(`/api/v1/tareas/${id}`, { estado }),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["tareas"] }),
  })

  const puedeMover = tienePermiso(usuario, "tarea:actualizar")
  const puedeAsignar = tienePermiso(usuario, "tarea:asignar")
  // Sin el permiso, `?vista=carga` cae al tablero en vez de dejar la pantalla en un
  // error: la URL la pudo escribir un enlace pegado por quien sí coordina.
  const vista = filtros.vista === "carga" && !puedeAsignar ? "estado" : filtros.vista
  const enLista = vista === "lista"
  // Estas dos traen sus propios datos —el resumen pide el panel y su mes, la carga
  // pide el agregado— y llevan sus tres estados dentro. Los de la consulta de arriba
  // no aplican: con la lista vacía, el mes que viene tiene que poder dibujarse igual.
  const aparte = vista === "resumen" || vista === "carga"

  // ⚠ **`vista` NO cuenta como filtro**, y contarla era un defecto que apareció al
  // hacer «Resumen» la vista por defecto: desde entonces `?vista=lista` se escribe
  // en la URL, así que una lista vacía sin ningún filtro puesto decía «No hay tareas
  // con esos filtros» y mandaba a buscar un filtro que nadie había tocado.
  const marcados = escribirFiltros(filtros)
  marcados.delete("vista")
  const hayFiltros = marcados.size > 0
  // En lista no se arrastra: el cambio de estado va por el selector de cada fila.
  const puedeSoltar = !enLista && puedeMover
  const tareas = consulta.data?.items ?? []
  // Contra lo que ya está cargado: la tarjeta sólo trae `tarea_padre_id`, y pedir la
  // macro por cada subtarea serían cinco peticiones para pintar cinco tarjetas.
  const codigoDe = (id: number) => tareas.find((t) => t.id === id)?.codigo

  const soltar = (clave: string, id: number) => {
    const tarea = tareas.find((t) => t.id === id)
    if (tarea === undefined || !puedeSoltar) return
    // Soltarla donde ya estaba no es un movimiento: escribirlo dejaría una entrada de
    // bitácora por cada arrastre que acabó donde empezó.
    if (tarea.estado === clave) return
    mover.mutate({ id, estado: clave as EstadoTarea })
  }

  const escribir = (f: Filtros) => {
    const siguiente = escribirFiltros(f)
    // `escribirFiltros` reconstruye la cadena desde los filtros y no sabe nada de la
    // ficha: sin esto, tocar un filtro la cerraría de golpe.
    if (enUrl !== null) siguiente.set("tarea", enUrl)
    setParams(siguiente, { replace: true })
  }
  const error = mover.error
  const panelCarga = useRef<HTMLDivElement | null>(null)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Tareas</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {consulta.data ? `${consulta.data.total} en total` : " "}
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-2">
          {/* El resumen es lo de uno y no es una lista: no se exporta. */}
          {vista === "carga" && (
            <Exportar
              titulo="Carga del equipo"
              subtitulo="Tareas abiertas por responsable"
              contenido={panelCarga}
            />
          )}
          {!aparte && tareas.length > 0 && (
            <Exportar
              titulo="Tareas"
              documento={() =>
                documentoDeTareas(
                  tareas,
                  nombreDe,
                  subtituloDeTareas(filtros, consulta.data?.total, tareas.length, nombreDe),
                )
              }
            />
          )}
          {/* Esconder el botón es COSMÉTICO: `can()` decide en cada petición. Sirve
              para no ofrecer algo que va a responder 403. */}
          {tienePermiso(usuario, "tarea:crear") && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              aria-haspopup="dialog"
              className="boton-primario flex h-9 items-center gap-2 px-3 text-sm font-medium"
            >
              <Plus size={16} aria-hidden="true" />
              Nueva tarea
            </button>
          )}
        </div>
      </div>

      <DialogoNuevaTarea
        abierto={creando}
        asignables={asignables}
        onCerrar={() => setCreando(false)}
      />

      {/* Las pestañas van ANTES de los filtros: primero qué se mira, después cómo
          se recorta. Al revés se lee como si el filtro cambiara de vista. */}
      <Pestanas
        pestanas={puedeAsignar ? VISTAS : VISTAS.filter((v) => v.valor !== "carga")}
        activa={vista}
        onCambiar={(vista) => escribir({ ...filtros, vista })}
        etiqueta="Vista del tablero"
        idPanel="panel-tareas"
        prefijoId="pestana"
      />

      {/* Ni el resumen ni la carga se recortan: el resumen es lo de uno y la carga
          cuenta el trabajo abierto del equipo entero. Unos filtros al lado que no
          tocan lo que se ve se leen como que sí lo tocan. */}
      {!aparte && <FiltrosTareas filtros={filtros} onCambiar={escribir} nombreDe={nombreDe} />}

      {!aparte && consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando tareas…
        </p>
      )}

      {!aparte && consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            {consulta.error instanceof ErrorApi
              ? consulta.error.message
              : "No se pudieron cargar las tareas"}
          </p>
        </div>
      )}

      {!aparte && consulta.data && tareas.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <ListChecks size={24} aria-hidden="true" />
          <p className="text-sm">
            {hayFiltros ? "No hay tareas con esos filtros" : "Todavía no hay tareas"}
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo mover la tarea"}
        </p>
      )}

      {aparte && (
        <div
          id="panel-tareas"
          role="tabpanel"
          aria-labelledby={`pestana-${vista}`}
          ref={panelCarga}
        >
          {vista === "resumen" ? (
            <ResumenTareas
              usuarioId={usuario?.id}
              onAbrir={abrirFicha}
              seleccionada={fichaVisible ? abierta : null}
            />
          ) : (
            <CargaEquipo
              onVer={(id) =>
                escribir({ ...filtros, vista: "lista", soloMias: false, asignadoA: id })
              }
            />
          )}
        </div>
      )}

      {!aparte && consulta.data && tareas.length > 0 && (
        <div
          id="panel-tareas"
          role="tabpanel"
          aria-labelledby={`pestana-${vista}`}
          // La ficha flota por encima del borde derecho, así que ni la tabla ni el
          // tablero ceden ancho al abrirla: las columnas no se reordenan bajo el
          // puntero de quien acaba de pulsar una tarjeta.
          className="space-y-6"
        >
          {enLista ? (
            <ListaTareas
              tareas={tareas}
              puedeMover={puedeMover}
              nombreDe={nombreDe}
              onAbrir={abrirFicha}
              seleccionada={fichaVisible ? abierta : null}
              onMover={(id, estado) => mover.mutate({ id, estado })}
            />
          ) : (
            <TableroTareas
              tareas={tareas}
              puedeMover={puedeMover}
              puedeSoltar={puedeSoltar}
              nombreDe={nombreDe}
              onAbrir={abrirFicha}
              seleccionada={fichaVisible ? abierta : null}
              onMover={(id, estado) => mover.mutate({ id, estado })}
              onSoltar={soltar}
              codigoDe={codigoDe}
            />
          )}
        </div>
      )}

      {/* La ficha NO es un modal. Comentar una tarea es mirarla junto a las demás
          —qué más tiene esa persona, qué vence antes— y una capa oscura tapa justo
          eso. Se puede pulsar otra tarjeta con la ficha abierta y cambia de tarea. */}
      <Cajon
        abierto={fichaVisible}
        titulo="Detalle"
        onCerrar={() => setParams(conFicha(null), { replace: true })}
      >
        {abierta !== null && (
          <PanelTarea
            tareaId={abierta}
            asignables={asignables}
            nombreDe={nombreDe}
            onAbrirOtra={abrirFicha}
          />
        )}
      </Cajon>
    </div>
  )
}
